import { api, type OrganisationChoice, type OrgOverview, type RestoreResult } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { askForDrive, preloadGoogleIdentity, uploadToDrive } from '../google-drive';
import { esc, fmtDate, html, toast } from '../ui';

/**
 * Taking the records out, and putting them back.
 *
 * Two different things wearing one heading. Taking a copy out is required:
 * ML.A.305(e) says the records go with the aircraft when it changes hands, and
 * ML.A.305(h) says they are kept for the life of the aircraft plus two years,
 * which nobody can promise about one supplier's database. So the file is the
 * operator's, in a format they can open without us.
 *
 * Putting one back is not required and is not a rollback. A restore lands
 * *beside* the live records as a second organisation, to be read and compared;
 * it never writes over what is there. That distinction is regulatory, not
 * technical — a continuing airworthiness record that can be silently replaced
 * by an older version is not a record, and ML.A.305(a) means what is on file
 * has to be what actually happened. So the copy is labelled a copy everywhere
 * it appears, and it can be thrown away when the comparison is done.
 */

/** What this deployment can offer besides the operator's own disk. */
export interface DriveDestination {
  available: boolean;
  clientId: string | null;
  scope: string;
}

export function recordsCard(
  o: OrgOverview,
  organisations: readonly OrganisationChoice[],
  drive: DriveDestination | null = null,
): string {
  const org = o.organisation;
  const copies = organisations.filter((c) => c.restoredAt && c.id !== org.id);

  return html`
    <details class="card" data-records>
      <summary class="card-h">
        <div class="sum">
          <span class="twist" aria-hidden="true"></span>
          <div>
            <h2>The records as a file</h2>
            <p class="dim tiny" style="margin:2px 0 0">
              A copy you keep yourself · handover on sale · reading an old backup back
            </p>
          </div>
        </div>
        <span class="lbl">ML.A.305(e), (h)</span>
      </summary>

      ${org.restoredAt
        ? callout('caution', '!', html`
            <b>You are looking at a copy, not the live records.</b> It was read back from a backup
            taken ${org.restoredFromTakenAt ? `on ${esc(fmtDate(org.restoredFromTakenAt))}` : 'earlier'},
            and nothing signed in here counts for anything. It holds no approval reference for that
            reason. Switch organisation to get back to the real one.
          `)
        : ''}

      <div class="rows" style="margin-top:12px">
        <div class="row">
          <div class="bd">
            <div class="t">Everything this organisation holds</div>
            <div class="m">
              Every record, every document, one file. Take one before an audit, before a migration,
              and on whatever schedule your CAME states — it is the copy that survives us.
            </div>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end">
            <button class="btn pri" data-backup-all>Download a backup</button>
            ${drive?.available ? html`<button class="btn" data-backup-drive>Save to Google Drive</button>` : ''}
          </div>
        </div>

        ${drive?.available
          ? html`
              <div class="row">
                <div class="bd">
                  <div class="t">About Google Drive</div>
                  <div class="m">
                    You sign in to your own Google account and the file goes from this page straight
                    to your Drive, into a folder called <b>FlyPack CAMO backups</b>. It never passes
                    through us on the way, and we keep no standing access to your Drive — the
                    permission lasts an hour and we can only ever see the files we put there
                    ourselves, never the rest of it. Each save asks again.
                  </div>
                </div>
              </div>
            `
          : ''}

        <div class="row">
          <div class="bd">
            <div class="t">One aircraft's records, to hand over</div>
            <div class="m">
              What ML.A.305(e) asks for when an aircraft is sold or moves to another organisation:
              its logbooks, its programme, its worksheets, its documents — and nothing about your
              other customers, your rates or your invoices.
            </div>
          </div>
          <button class="btn" data-backup-aircraft>One aircraft</button>
        </div>

        <div class="row">
          <div class="bd">
            <div class="t">Read a backup back</div>
            <div class="m">
              Opens it as a second organisation beside this one, to look at and compare. It never
              writes over the live records — see below for why not.
            </div>
          </div>
          <button class="btn" data-restore>Restore from a file</button>
        </div>

        ${copies.map((c) => html`
          <div class="row">
            <div class="bd">
              <div class="t">Copy: ${esc(c.name)}</div>
              <div class="m">
                Restored ${esc(fmtDate(c.restoredAt ?? ''))}. Throw it away once you have finished
                reading it — it is a full second set of records and it should not outlive the
                question it was opened to answer.
              </div>
            </div>
            <button class="btn danger" data-discard="${esc(c.id)}" data-name="${esc(c.name)}">Discard it</button>
          </div>
        `).join('')}
      </div>

      ${explainer()}
    </details>
  `;
}

/**
 * Why a restore does not overwrite.
 *
 * Asked every time, and the answer is the one thing about this feature that is
 * not obvious — so it sits on the screen rather than in a manual.
 */
function explainer(): string {
  return html`
    <details class="card-f" style="cursor:pointer">
      <summary><b>Is restoring a backup allowed?</b></summary>
      <div style="margin-top:10px;display:flex;flex-direction:column;gap:12px;cursor:default">
        <div class="rows">
          <div class="row">
            <div class="bd">
              <div class="t">Taking a copy out: expected</div>
              <div class="m">
                ML.A.305(h) puts the retention on the operator — twelve months after the work for a
                worksheet, the life of the aircraft plus two years for the logbooks. Holding that
                only inside one supplier's system is a risk somebody eventually has to answer for.
                A file you keep is the answer.
              </div>
            </div>
          </div>
          <div class="row">
            <div class="bd">
              <div class="t">Putting one back over the live records: not allowed</div>
              <div class="m">
                A restore that replaced today's records with last month's would erase work that was
                signed for. ML.A.305(a) requires the record to reflect what was done, and 145.A.55
                requires it to stay legible and traceable for the retention period. So this does not
                offer it, and there is no button anywhere that does.
              </div>
            </div>
          </div>
          <div class="row">
            <div class="bd">
              <div class="t">Opening one beside the live records: fine, and useful</div>
              <div class="m">
                It is reading an archive. The copy is a separate organisation, marked as a copy,
                with no approval reference — so nothing in it can be issued or released. Use it to
                answer "what did the file say in March", take back a document somebody deleted, or
                check a migration landed. Then discard it.
              </div>
            </div>
          </div>
        </div>

        ${callout('info', 'i', html`
          <b>If something really was lost, the fix is a correction, not a rewind.</b> Restore the
          copy, find what is missing, and re-enter it in the live records as a correction — which
          leaves an audit trail saying who put it back and when. That trail is what an inspector
          asks about, and a silent restore would have destroyed it.
        `)}
      </div>
    </details>
  `;
}

export function bindRecords(
  host: HTMLElement,
  o: OrgOverview,
  reload: () => void,
  drive: DriveDestination | null = null,
): void {
  host.querySelector<HTMLButtonElement>('[data-backup-all]')?.addEventListener('click', (e) => {
    e.preventDefault();
    void downloadBackup(e.currentTarget as HTMLButtonElement, undefined);
  });

  const toDrive = host.querySelector<HTMLButtonElement>('[data-backup-drive]');
  if (toDrive && drive?.clientId) {
    // Fetched now rather than on the click. Google's permission window is a
    // popup, and a popup is only allowed while the browser still counts the
    // click as recent — waiting for a script to arrive over the network first
    // is exactly how it stops being recent, and then nothing happens at all.
    preloadGoogleIdentity();
    toDrive.addEventListener('click', (e) => {
      e.preventDefault();
      void saveToDrive(toDrive, drive.clientId!);
    });
  }

  host.querySelector<HTMLButtonElement>('[data-backup-aircraft]')?.addEventListener('click', (e) => {
    e.preventDefault();
    void handoverDialog();
  });

  host.querySelector<HTMLButtonElement>('[data-restore]')?.addEventListener('click', (e) => {
    e.preventDefault();
    restoreDialog(o, reload);
  });

  host.querySelectorAll<HTMLButtonElement>('[data-discard]').forEach((b) =>
    b.addEventListener('click', (e) => {
      e.preventDefault();
      discardDialog(b.dataset.discard!, b.dataset.name ?? 'that copy', o.organisation.id, reload);
    }),
  );
}

/**
 * The file can take a while to build, so the button says so.
 *
 * Without this the page looks dead for however long a few hundred documents
 * take to read, and the second click starts a second build.
 */
async function downloadBackup(button: HTMLButtonElement, aircraftId: string | undefined): Promise<string | null> {
  const was = button.textContent;
  button.disabled = true;
  button.textContent = 'Building…';
  try {
    const fileName = await api.organisation.backup(aircraftId);
    toast(`${fileName} — saved to your downloads`);
    return fileName;
  } catch (err) {
    toast((err as Error).message || 'Could not build that backup', 'error');
    return null;
  } finally {
    button.disabled = false;
    button.textContent = was;
  }
}

/**
 * Permission first, then the file.
 *
 * That order is not tidiness. Building a busy operator's archive takes the
 * better part of a minute, and asking Google for permission after that means
 * asking long after the click — which browsers refuse. So: ask while the click
 * is still warm, then build, then upload. The token is good for an hour, which
 * is far longer than any of this takes.
 */
async function saveToDrive(button: HTMLButtonElement, clientId: string): Promise<void> {
  const was = button.textContent;
  const say = (text: string) => {
    button.textContent = text;
  };

  button.disabled = true;
  try {
    say('Asking Google…');
    const token = await askForDrive(clientId);

    say('Building the file…');
    const { fileName, blob } = await api.organisation.buildBackup();

    say('Uploading…');
    const saved = await uploadToDrive(token, fileName, blob, (fraction) =>
      say(`Uploading ${Math.round(fraction * 100)}%`),
    );

    // Where it actually went, not where it was meant to go. Saying "in
    // FlyPack CAMO backups" when the folder was never created sent somebody
    // looking through their Drive for a folder that did not exist.
    toast(
      saved.folder
        ? `${saved.name} saved to your Google Drive, in "${saved.folder}"`
        : `${saved.name} saved to the main folder of your Google Drive${saved.folderProblem ? ` — no folder was made: ${saved.folderProblem}` : ''}`,
    );
    if (saved.link) window.open(saved.link, '_blank', 'noopener');
  } catch (err) {
    toast((err as Error).message || 'Could not save to Google Drive', 'error');
  } finally {
    button.disabled = false;
    say(was ?? 'Save to Google Drive');
  }
}

/** One aircraft's record set, for the next operator. */
async function handoverDialog(): Promise<void> {
  const fleet = await api.fleet().catch(() => []);
  if (!fleet.length) {
    toast('No aircraft to hand over', 'error');
    return;
  }

  openDialog<{ aircraftId: string }>({
    title: "One aircraft's records",
    sub: 'What goes with the aircraft when it changes hands',
    primary: 'Build the file',
    body: () => {
      return html`
        ${field('Aircraft', select('aircraftId', fleet.map((a) => opt(a.id, `${a.registration} — ${a.type}`)).join('')))}
        ${callout('info', 'i', html`
          Its logbooks, airworthiness review records, programme, worksheets, components and
          documents — the set ML.A.305(e) says transfers with the aircraft. Your rate card,
          invoices and other customers stay behind, because they are not part of the aircraft's
          record and the next operator has no business holding them.
        `)}
        <p class="tiny dim" style="margin:0">
          Hand it over with the aircraft. Keep your own copy too: ML.A.305(h) does not stop
          applying to the period you managed it.
        </p>
      `;
    },
    collect: (root) => {
      const aircraftId = val(root, 'aircraftId');
      if (!aircraftId) return { error: 'Which aircraft?' };
      return { aircraftId };
    },
    submit: async (payload) => ({ fileName: await api.organisation.backup(payload.aircraftId) }),
    done: (r: { fileName: string }) => `${r.fileName} — saved to your downloads`,
  });
}

/** Reading one back, beside the live records. */
function restoreDialog(o: OrgOverview, reload: () => void): void {
  openDialog<{ file: File; name: string }>({
    wide: true,
    title: 'Restore from a backup',
    sub: 'Opened beside the live records, never over them',
    primary: 'Read it back',
    body: () => html`
      ${field('The backup file', input('file', '', 'type="file" accept=".zip,application/zip"'),
        'The .zip this organisation produced. Nothing else will be accepted.')}
      ${field('Call the copy', input('name', `${o.organisation.name} — copy`, 'maxlength="120"'),
        'How it will appear in the organisation list. Say what it is for, so it is obvious later')}
      ${callout('caution', '!', html`
        <b>This creates a second organisation.</b> The live records are not touched, read or
        changed. The copy has no approval reference and nothing in it can be issued or released —
        it is there to be read and compared, and discarded afterwards.
      `)}
      <p class="tiny dim" style="margin:0">
        A large backup takes a minute or two: every document in it is written back to disk as well
        as every record. Leave this open until it finishes.
      </p>
    `,
    collect: (root) => {
      const file = root.querySelector<HTMLInputElement>('[name="file"]')?.files?.[0];
      if (!file) return { error: 'Choose the backup file to read back.' };
      const name = val(root, 'name');
      if (!name) return { error: 'Give the copy a name, so it can be told apart from the live one.' };
      return { file, name };
    },
    submit: (payload) => api.organisation.restoreBackup(payload.file, payload.name),
    done: (r: RestoreResult) => {
      const failed = r.failed?.length
        ? ` · could not put back: ${r.failed.map((f) => `${f.table} (${f.rows})`).join(', ')}`
        : '';
      return `${r.name} — ${r.rows} record(s) and ${r.documents} document(s) read back${failed}`;
    },
    /**
     * Re-read the session, naming the organisation we are already in.
     *
     * The copy is a new organisation and the list of them lives in the
     * session, so it has to be refreshed before the row offering to discard it
     * can appear. Naming the current one matters: a plain refresh after a
     * restore finds a login with two organisations and no answer to which,
     * which ends the session and puts "Unauthorized" on screen — the first
     * thing this did, right after a restore that had actually worked.
     */
    onDone: () => void api.selectOrganisation(o.organisation.id).then(reload),
  });
}

/** Throwing a copy away once it has answered its question. */
function discardDialog(id: string, name: string, hereId: string, reload: () => void): void {
  openDialog<{ ok: true }>({
    title: 'Discard this copy',
    sub: esc(name),
    primary: 'Discard it',
    body: () => html`
      ${callout('caution', '!', html`
        Everything in the copy goes — its records, its documents, its audit trail. The live
        records are not affected in any way, and neither is the backup file it came from.
      `)}
      <p class="tiny dim" style="margin:0">
        This cannot be undone from here. If you need it again, read the same backup file back.
      </p>
    `,
    collect: () => ({ ok: true as const }),
    submit: () => api.organisation.discardRestored(id),
    done: (r: { name: string; rows: number }) => `${r.name} discarded — ${r.rows} row(s) removed`,
    // The copy has just left the session's list of organisations, and the card
    // is drawn from that list — without this it keeps offering to discard
    // something that is already gone.
    onDone: () => void api.selectOrganisation(hereId).then(reload),
  });
}
